import React, {useState} from 'react';
import {Col, Row, showConfirm, Icon, Text, toaster} from '@mc/ui-v4';
import {Accept, useDropzone} from 'react-dropzone';
import {FieldRenderProps} from 'react-final-form';
import {mcNew} from '@mc/react-ui-icons';
import './DocumentDropzone.scss';
import {DocumentRow, DocumentTable} from './DocumentTable';
import {DocumentApi} from '../../../api';
import {useQuery} from 'react-query';

type DocumentDropzoneValue = string[];

export type DocumentDropzoneProps = {
    onDocumentUpload: (document: File) => Promise<string>;
    getDocumentUrl: (documentId: string) => string;
    onFocus?: (val?: DocumentDropzoneValue) => void;
    onBlur?: (val?: DocumentDropzoneValue) => void;
    onChange?: (val?: DocumentDropzoneValue) => void;
    disabled?: boolean;
    readOnly?: boolean;
    value?: DocumentDropzoneValue;
    fileObjects?: DocumentRow<string>[];
    setFileObjects?: React.Dispatch<React.SetStateAction<DocumentRow<string>[]>>;
    multiple?: boolean;
    maxFiles?: number;
} & FieldRenderProps<DocumentDropzoneValue>;

export const DocumentDropzone: React.FC<DocumentDropzoneProps> = ({
    getDocumentUrl,
    onDocumentUpload,
    onChange,
    value,
    readOnly = false,
    disabled = false,
    multiple = false,
    maxFiles = 0,
    fileObjects,
    setFileObjects,
}) => {
    const getTypyDokumentu = async () => (await DocumentApi.getTypyDokumentu()).data;
    const typyDokumentu = useQuery('typyDokumentu', getTypyDokumentu).data;
    // hotfix MCW-9444 - absolutně netuším co tam může a taky nemusí být, mělo by se to udělat pořádně
    const filteredTypyDokumentu = typyDokumentu?.filter(typ => typ.kod.includes('DOKUMENT'));

    if (!Array.isArray(value)) {
        value = [];
    }

    const readFile = async (file: File): Promise<string> => {
        return await new Promise(resolve => {
            const reader = new FileReader();
            reader.onabort = () => console.log('file reading was aborted', file);
            reader.onerror = () => console.log('file reading has failed', file);
            reader.onload = () => {
                onDocumentUpload(file).then(documentId => {
                    setFileObjects(prevFileObjects => [
                        ...prevFileObjects,
                        {
                            id: documentId,
                            nazev: file.name,
                            kodTypDokument: filteredTypyDokumentu ? filteredTypyDokumentu[0].kod : 'Neznámý',
                            popis: '',
                        },
                    ]);
                    resolve(documentId);
                });
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const handleOnDrop = async (files: File[]) => {
        const documentIds = [...value];
        for (let i = 0; i < files.length; i++) {
            documentIds.push(await readFile(files[i]));
        }

        onChange && onChange(documentIds);
    };

    const handleDelete = async (e: React.MouseEvent<HTMLElement, MouseEvent>, documentId: string) => {
        e.stopPropagation();
        const result = await showConfirm({
            title: 'Odstranit dokument',
            message: 'Opravdu chcete odstranit dokument?',
            intent: 'danger',
            confirmText: 'Odstranit',
        });
        if (result) {
            const filteredFileObjects = fileObjects.filter(x => x.id !== documentId);
            setFileObjects(filteredFileObjects);
            onChange(value.filter(x => x !== documentId));
        }
    };

    const handleUpdate = (documentId: string, key: string, value: string) => {
        const updatedFileObjects = fileObjects.map(x => (x.id === documentId ? {...x, [key]: value} : x));
        setFileObjects(updatedFileObjects);
    };

    const acceptedTypes: Accept = {
        'application/pdf': [],
        'application/msword': [],
        'application/vnd.ms-excel': [],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
    };
    const {getRootProps, getInputProps} = useDropzone({
        accept: acceptedTypes,
        multiple: multiple,
        maxFiles: maxFiles,
        preventDropOnDocument: true,
        onDrop: handleOnDrop,
        onDropRejected: () => toaster.warning('Nepovolený formát souboru'),
    });

    let minHeight = value.length > 0 ? `${28 * value.length + 34}px` : '';

    return (
        <div className={'document-dropzone'}>
            <Row>
                <Col>
                    {readOnly || disabled || (
                        <div {...getRootProps({className: 'dropzone'})}>
                            <input {...getInputProps()} />
                            <Text intent={'primary'} className={'my-auto'}>
                                <Icon icon={mcNew} className={'mr-2'} />
                                {'Přetáhněte soubor nebo klikněte pro výběr.'}
                            </Text>
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col style={{minHeight: minHeight}}>
                    {fileObjects && fileObjects.length > 0 && (
                        <DocumentTable
                            documents={fileObjects}
                            handleDelete={handleDelete}
                            handleUpdate={handleUpdate}
                            getDocumentUrl={getDocumentUrl}
                            typyDokumentu={filteredTypyDokumentu}
                            editable
                        />
                    )}
                </Col>
            </Row>
        </div>
    );
};
